import { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";

// Detects whether the site is being viewed inside an installed PWA already.
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// A small, dismissible "Install Reads" button. Behaviour:
//  - On Android / Chromium: listens for `beforeinstallprompt` and lets the
//    user launch the native install prompt with one tap.
//  - On iOS Safari: those browsers don't fire that event — we render a
//    hint explaining the manual Share → Add to Home Screen path instead.
//  - When the app is already installed (`display-mode: standalone`) the
//    button hides itself.
export function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (installed) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  if (installed) return null;

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* user dismissed */ }
      setDeferred(null);
    } else if (isIos) {
      setShowIosHint((v) => !v);
    }
  };

  // If neither a deferred prompt nor iOS, don't render anything —
  // the browser doesn't support PWA install here.
  if (!deferred && !isIos) return null;

  return (
    <div className="border border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] p-5 flex items-start gap-4" data-testid="install-app-card">
      <div className="w-10 h-10 rounded-sm bg-[#0A0A0A] text-[#F2EAD3] flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-lg text-[#0A0A0A] tracking-tight">Install Reads on your phone</div>
        <p className="text-sm text-[#4A4030] mt-1 leading-relaxed">
          Add Reads to your home screen so it opens like a real app — no browser bar, no address bar, straight to your shelf.
        </p>
        <button
          onClick={onClick}
          className="mt-3 inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[11px] px-4 py-2 rounded-sm transition-colors"
          data-testid="install-app-button"
        >
          {deferred ? <><Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Install</>
                    : <><Share className="w-3.5 h-3.5" strokeWidth={1.5} /> How to install on iOS</>}
        </button>
        {showIosHint && (
          <div className="mt-3 text-sm text-[#0A0A0A] bg-[#F2EAD3] border border-[rgba(10,10,10,0.10)] p-3 rounded-sm" data-testid="install-ios-hint">
            <ol className="list-decimal pl-5 space-y-1">
              <li>Tap the <strong>Share</strong> icon at the bottom of Safari (the square with an up arrow).</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong> in the top-right corner. Done — Reads now lives on your home screen.</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
